import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard, Loader2, Wallet } from 'lucide-react';
import styles from './TopupConfirmDialog.module.css';

export interface TopupConfirmDialogProps {
  /** The amount, already formatted in its currency ("CA$25.00"). */
  amount: string;
  /** The top-up's currency code ("CAD"), printed beside the amount. */
  currency: string;
  /** The card that will be charged, or null when the list has not loaded. */
  card: { brand: string | null; last4: string | null } | null;
  /** The wallet that receives the money ("CAD wallet"). */
  walletLabel: string;
  /** The charge is running: both buttons are disabled. */
  charging: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The one step before a wallet top-up charges the card (Warren, 2026-10-25:
 * "There should be a confirmation before topping up"). States the amount and
 * its currency, the card that will be charged and the wallet that receives
 * it, with Confirm and Cancel. Confirm is disabled while the charge runs
 * (the flow also refuses a second Confirm, topupFlow.ts). Cancel, the
 * backdrop and Escape close it and charge nothing, except while charging.
 */
export function TopupConfirmDialog({ amount, currency, card, walletLabel, charging, onConfirm, onCancel }: TopupConfirmDialogProps) {
  const { t } = useTranslation('wallet');
  const cancelRef = useRef<HTMLButtonElement>(null);
  const latest = useRef({ charging, onCancel });
  latest.current = { charging, onCancel };

  // Focus starts on Cancel (once), so an Enter press already in flight
  // cannot confirm a charge by accident; Escape cancels.
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !latest.current.charging) latest.current.onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const cardText = card?.last4
    ? card.brand
      ? t('confirm.cardEndingBrand', { brand: card.brand, last4: card.last4 })
      : t('confirm.cardEnding', { last4: card.last4 })
    : t('confirm.cardDefault');

  const dialog = (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="evi-topup-confirm-title"
      onClick={() => { if (!charging) onCancel(); }}
    >
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <h2 id="evi-topup-confirm-title" className={styles.title}>{t('confirm.title')}</h2>

        <dl className={styles.rows}>
          <div className={styles.row}>
            <dt className={styles.rowLabel}>{t('confirm.amount')}</dt>
            <dd className={styles.amount} data-topup-amount>
              {amount}<span className={styles.amountCurrency}> {currency}</span>
            </dd>
          </div>
          <div className={styles.row}>
            <dt className={styles.rowLabel}>{t('confirm.card')}</dt>
            <dd className={styles.rowValue} data-topup-card>
              <CreditCard size={14} aria-hidden /> {cardText}
            </dd>
          </div>
          <div className={styles.row}>
            <dt className={styles.rowLabel}>{t('confirm.wallet')}</dt>
            <dd className={styles.rowValue} data-topup-wallet>
              <Wallet size={14} aria-hidden /> {walletLabel}
            </dd>
          </div>
        </dl>

        <p className={styles.note}>{t('confirm.note')}</p>

        <div className={styles.actions}>
          <button
            ref={cancelRef}
            type="button"
            className={styles.cancelBtn}
            onClick={onCancel}
            disabled={charging}
          >
            {t('confirm.cancel')}
          </button>
          <button
            type="button"
            className={styles.confirmBtn}
            onClick={onConfirm}
            disabled={charging}
            aria-busy={charging}
          >
            {charging
              ? <><Loader2 size={14} className={styles.spin} aria-hidden /> {t('topup.charging')}</>
              : t('confirm.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
  // On document.body, above the app shell's header and floating nav (the
  // page content sits in a lower stacking context). Inline where there is
  // no document (server-side render in tests).
  return typeof document !== 'undefined' ? createPortal(dialog, document.body) : dialog;
}
